(() => {
  'use strict';

  const ELEMENT_NAME = 'kdk-product-recommender';

  if (customElements.get(ELEMENT_NAME)) return;

  class KdkProductRecommender extends HTMLElement {
    constructor() {
      super();

      this.track = this.querySelector('[data-kdk-recommender-track]');
      this.viewport = this.querySelector('[data-kdk-recommender-viewport]');
      this.previousButton = this.querySelector('[data-kdk-recommender-previous]');
      this.nextButton = this.querySelector('[data-kdk-recommender-next]');
      this.status = this.querySelector('[data-kdk-recommender-status]');
      this.configElement = this.querySelector('[data-kdk-recommender-config]');

      this.productId = String(this.dataset.productId || '');
      this.maxProducts = Math.max(1, Number.parseInt(this.dataset.maxProducts || '7', 10));
      this.manualCount = Math.max(0, Number.parseInt(this.dataset.manualCount || '0', 10));
      this.searchUrl = this.dataset.searchUrl || '/search';
      this.sectionId = this.dataset.resultsSectionId || 'kdk-product-recommender-results';
      this.cacheVersion = this.dataset.cacheVersion || '1';
      this.hasLoaded = false;
      this.resizeObserver = null;
      this.intersectionObserver = null;

      this.config = this.parseConfig();

      this.onScroll = this.updateNavigation.bind(this);
      this.onResize = this.updateNavigation.bind(this);
      this.onPrevious = () => this.scrollByCard(-1);
      this.onNext = () => this.scrollByCard(1);
    }

    connectedCallback() {
      if (!this.track || !this.viewport) return;

      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.viewport);
      } else {
        window.addEventListener('resize', this.onResize, { passive: true });
      }

      this.updateNavigation();

      if (this.manualCount >= this.maxProducts || this.config.collections.length === 0) {
        this.finishLoading();
        return;
      }

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              this.intersectionObserver?.disconnect();
              this.loadRecommendations();
            }
          },
          { rootMargin: '700px 0px' }
        );
        this.intersectionObserver.observe(this);
      } else {
        this.loadRecommendations();
      }
    }

    disconnectedCallback() {
      this.viewport?.removeEventListener('scroll', this.onScroll);
      this.previousButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      window.removeEventListener('resize', this.onResize);
    }

    parseConfig() {
      try {
        const parsed = JSON.parse(this.configElement?.textContent || '{}');
        return {
          collections: Array.isArray(parsed.collections) ? parsed.collections.filter(Boolean) : [],
          colors: Array.isArray(parsed.colors) ? parsed.colors.filter(Boolean) : [],
          formats: Array.isArray(parsed.formats) ? parsed.formats.filter(Boolean) : []
        };
      } catch (error) {
        console.warn('[KDK recommender] Invalid configuration.', error);
        return { collections: [], colors: [], formats: [] };
      }
    }

    async loadRecommendations() {
      if (this.hasLoaded) return;
      this.hasLoaded = true;

      const selectedCollection = this.pickCollection();
      if (!selectedCollection?.name) {
        this.finishLoading();
        return;
      }

      try {
        const candidateMap = new Map();
        const manualIds = this.manualProductIds();
        const remainingCapacity = Math.max(0, this.maxProducts - manualIds.size);
        const selectedCollectionKey = String(selectedCollection.key || '');

        const isEligibleForSelectedCollection = (candidate) => (
          candidate.id !== this.productId &&
          !manualIds.has(candidate.id) &&
          candidate.collectionKeys.has(selectedCollectionKey)
        );

        const queryPlan = [];
        const hasFormats = this.config.formats.some((format) => format?.name);
        const hasColors = this.config.colors.some((color) => color?.name);

        if (hasFormats) {
          queryPlan.push({
            query: this.buildQuery(selectedCollection.name, { includeFormats: true }),
            type: 'format'
          });
        }

        if (hasColors) {
          queryPlan.push({
            query: this.buildQuery(selectedCollection.name, { includeColors: true }),
            type: 'colors'
          });
        }

        queryPlan.push({
          query: this.buildQuery(selectedCollection.name),
          type: 'collection'
        });

        for (const queryStep of queryPlan) {
          if (candidateMap.size >= remainingCapacity) break;
          if (!queryStep.query) continue;

          const candidates = await this.fetchCandidates(queryStep.query, queryStep.type);
          candidates
            .filter(isEligibleForSelectedCollection)
            .forEach((candidate) => {
              if (!candidateMap.has(candidate.id)) candidateMap.set(candidate.id, candidate);
            });
        }

        const recommendations = this.rankCandidates(Array.from(candidateMap.values()));
        this.insertCandidates(recommendations);
      } catch (error) {
        console.warn('[KDK recommender] Could not load collection recommendations.', error);
      } finally {
        this.finishLoading();
      }
    }

    pickCollection() {
      const collections = this.config.collections;
      if (collections.length === 1) return collections[0];

      const randomIndex = this.randomInteger(collections.length);
      return collections[randomIndex];
    }

    randomInteger(maximum) {
      if (maximum <= 1) return 0;

      if (window.crypto?.getRandomValues) {
        const values = new Uint32Array(1);
        window.crypto.getRandomValues(values);
        return values[0] % maximum;
      }

      return Math.floor(Math.random() * maximum);
    }

    buildQuery(collectionName, options = {}) {
      const collectionTerm = `tag:${this.quoteSearchValue(collectionName)}`;
      const exclusions = ['Combo', 'Mural', 'Outlet']
        .map((tag) => `-tag:${this.quoteSearchValue(tag)}`)
        .join(' ');
      const qualifierGroups = [];

      if (options.includeFormats) {
        const formatTerms = this.config.formats
          .filter((format) => format?.name)
          .map((format) => `tag:${this.quoteSearchValue(format.name)}`);

        if (formatTerms.length === 0) return '';
        qualifierGroups.push(formatTerms);
      }

      if (options.includeColors) {
        const colorTerms = this.config.colors
          .filter((color) => color?.name)
          .map((color) => `tag:${this.quoteSearchValue(color.name)}`);

        if (colorTerms.length === 0) return '';
        qualifierGroups.push(colorTerms);
      }

      const qualifiers = qualifierGroups
        .map((group) => `AND (${group.join(' OR ')})`)
        .join(' ');

      return `${collectionTerm} ${qualifiers} ${exclusions}`.replace(/\s+/g, ' ').trim();
    }

    quoteSearchValue(value) {
      const escaped = String(value)
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"');
      return `"${escaped}"`;
    }

    async fetchCandidates(query, queryType) {
      const url = new URL(this.searchUrl, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'none');
      url.searchParams.set('sort_by', 'relevance');
      url.searchParams.set('section_id', this.sectionId);
      this.copyThemePreviewParameters(url);

      const cacheKey = this.cacheKey(url, queryType);
      const cached = this.readCache(cacheKey);
      if (cached) return cached;

      const response = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const candidates = this.parseCandidates(html);
      this.writeCache(cacheKey, candidates);
      return candidates;
    }

    copyThemePreviewParameters(url) {
      const currentParameters = new URLSearchParams(window.location.search);
      ['preview_theme_id', '_fd', 'pb'].forEach((parameter) => {
        const value = currentParameters.get(parameter);
        if (value) url.searchParams.set(parameter, value);
      });
    }

    parseCandidates(html) {
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const templates = documentFragment.querySelectorAll('[data-kdk-recommender-candidate]');

      return Array.from(templates).map((template) => ({
        id: String(template.dataset.productId || ''),
        colorKeys: this.parseKeySet(template.dataset.colorKeys || ''),
        formatKeys: this.parseKeySet(template.dataset.formatKeys || ''),
        collectionKeys: this.parseKeySet(template.dataset.collectionKeys || ''),
        html: template.innerHTML.trim()
      })).filter((candidate) => candidate.id && candidate.html);
    }

    parseKeySet(value) {
      return new Set(String(value).split('|').filter(Boolean));
    }

    rankCandidates(candidates) {
      const manualIds = this.manualProductIds();
      const currentColorKeys = new Set(
        this.config.colors.map((color) => String(color?.key || '')).filter(Boolean)
      );
      const currentFormatKeys = new Set(
        this.config.formats.map((format) => String(format?.key || '')).filter(Boolean)
      );

      return candidates
        .filter((candidate) => candidate.id !== this.productId && !manualIds.has(candidate.id))
        .map((candidate) => ({
          ...candidate,
          formatScore: Array.from(candidate.formatKeys).reduce(
            (total, key) => total + (currentFormatKeys.has(key) ? 1 : 0),
            0
          ),
          colorScore: Array.from(candidate.colorKeys).reduce(
            (total, key) => total + (currentColorKeys.has(key) ? 1 : 0),
            0
          ),
          tieBreaker: this.stableHash(`${this.productId}:${candidate.id}`)
        }))
        .sort((first, second) => {
          if (second.formatScore !== first.formatScore) return second.formatScore - first.formatScore;
          if (second.colorScore !== first.colorScore) return second.colorScore - first.colorScore;
          return first.tieBreaker - second.tieBreaker;
        })
        .slice(0, Math.max(0, this.maxProducts - manualIds.size));
    }

    manualProductIds() {
      const ids = new Set();
      this.track?.querySelectorAll('[data-kdk-product-id]').forEach((card) => {
        const id = String(card.dataset.kdkProductId || '');
        if (id) ids.add(id);
      });
      return ids;
    }

    stableHash(value) {
      let hash = 2166136261;
      for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
      }
      return hash >>> 0;
    }

    insertCandidates(candidates) {
      const fragment = document.createDocumentFragment();

      candidates.forEach((candidate) => {
        const template = document.createElement('template');
        template.innerHTML = candidate.html;
        const card = template.content.firstElementChild;
        if (card) fragment.appendChild(card);
      });

      this.removePlaceholders();
      this.track.appendChild(fragment);
    }

    finishLoading() {
      this.removePlaceholders();
      this.removeAttribute('aria-busy');

      const cardCount = this.track?.querySelectorAll('[data-kdk-product-id]').length || 0;
      if (cardCount === 0) {
        this.hidden = true;
        return;
      }

      if (this.status) this.status.textContent = String(cardCount);
      requestAnimationFrame(() => this.updateNavigation());
    }

    removePlaceholders() {
      this.querySelectorAll('[data-kdk-recommender-placeholder]').forEach((placeholder) => placeholder.remove());
    }

    scrollByCard(direction) {
      const card = this.track?.querySelector('[data-kdk-product-id]');
      if (!card || !this.viewport) return;

      const trackStyles = window.getComputedStyle(this.track);
      const gap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap || '0') || 0;
      this.viewport.scrollBy({
        left: direction * (card.getBoundingClientRect().width + gap),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    updateNavigation() {
      if (!this.viewport || !this.previousButton || !this.nextButton) return;

      const maximumScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const hasOverflow = maximumScroll > 2;
      const currentScroll = Math.abs(this.viewport.scrollLeft);

      this.previousButton.hidden = !hasOverflow || currentScroll <= 2;
      this.nextButton.hidden = !hasOverflow || currentScroll >= maximumScroll - 2;
    }

    cacheKey(url, queryType) {
      const locale = document.documentElement.lang || 'default';
      const previewThemeId = url.searchParams.get('preview_theme_id') || 'published';
      return `kdk-recommender:${this.cacheVersion}:${locale}:${url.pathname}:${previewThemeId}:${queryType}:${url.searchParams.get('q')}`;
    }

    readCache(key) {
      try {
        const raw = window.sessionStorage.getItem(key);
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return null;

        return parsed.map((candidate) => ({
          id: String(candidate.id || ''),
          colorKeys: new Set(Array.isArray(candidate.colorKeys) ? candidate.colorKeys : []),
          formatKeys: new Set(Array.isArray(candidate.formatKeys) ? candidate.formatKeys : []),
          collectionKeys: new Set(Array.isArray(candidate.collectionKeys) ? candidate.collectionKeys : []),
          html: String(candidate.html || '')
        })).filter((candidate) => candidate.id && candidate.html);
      } catch (_) {
        return null;
      }
    }

    writeCache(key, candidates) {
      try {
        const serializable = candidates.map((candidate) => ({
          id: candidate.id,
          colorKeys: Array.from(candidate.colorKeys),
          formatKeys: Array.from(candidate.formatKeys),
          collectionKeys: Array.from(candidate.collectionKeys),
          html: candidate.html
        }));
        window.sessionStorage.setItem(key, JSON.stringify(serializable));
      } catch (_) {
        // Storage can be unavailable or full; recommendations still work without cache.
      }
    }
  }

  customElements.define(ELEMENT_NAME, KdkProductRecommender);
})();
